// @flow strict
import * as React from 'react'
import idx from 'idx'
import { useEffect, useState } from 'react'
import { graphql, Link, withPrefix } from 'gatsby'
import {
  PersonDetails,
  SEOHead,
  type Location,
} from '@mondough/public-web-components'

import AdjustImpressionPixel from '../../components/AdjustImpressionPixel/AdjustImpressionPixel.jsx'
import BlogHeader from '../../components/BlogHeader/BlogHeader'
import SocialWidget from '../../components/SocialWidget'
import Global from '../../Global'

import styles from './BlogPost.module.scss'

import { returnFirstWord, slugifyString } from '../../utils/stringUtils/es6'
import setDimensionsFirePageview from '../../utils/googleAnalytics.js'
import richTextToReact from '../../utils/richTextToReact'

// TYPE DECLARATIONS
import {
  type singleBlogPost,
  type singleBlogPost_contentfulWebBlogPost_authors,
} from '../../../__types__/singleBlogPost'
import objectToQueryString from '@mondough/public-web-components/src/utils/objectToQueryString'

type Props = {
  data: singleBlogPost,
  location: Location,
  pageContext: {
    slug: string,
  },
}
type AuthorProps = {|
  name: string,
  title: string,
  image: string,
  linkUrl: string,
  linkText: string,
|}

const generateAuthorProps = (
  author: ?singleBlogPost_contentfulWebBlogPost_authors,
): AuthorProps => {
  const { name, jobTitle, profilePicture } = author || {}
  const linkUrl = `/authors/${name ? slugifyString(name) : ''}`
  const linkText = `Read more from ${name ? returnFirstWord(name) : ''}`

  return {
    name: name || '',
    title: (jobTitle && jobTitle.text) || '',
    image: idx(profilePicture, _ => _.file.url) || '',
    linkUrl,
    linkText,
  }
}

const Author = ({ author }: {| author: AuthorProps |}) => {
  return (
    <PersonDetails
      image={
        <img
          src={`${author.image}${objectToQueryString(
            // Note: these parameters only work for images served by the contentful images api
            // See: https://www.contentful.com/developers/docs/references/images-api/
            { w: 150, fm: 'jpg', q: '95' },
            { startWithQuestionMark: true },
          )}`}
          alt={author.name}
        />
      }
    >
      <PersonDetails.Heading>{author.name}</PersonDetails.Heading>
      <PersonDetails.Text>{author.title}</PersonDetails.Text>
      <PersonDetails.Link as={<Link to={author.linkUrl} />}>
        {author.linkText}
      </PersonDetails.Link>
    </PersonDetails>
  )
}

/***************************
 *          BLOG POST      *
 ***************************/

const BlogPost = ({
  pageContext: { slug },
  location: { pathname },
  data: { contentfulWebBlogPost: blogPost },
}: Props) => {
  const {
    title,
    description,
    authors,
    blogContent,
    startDate,
    category,
    discussionId,
    webSlug,
    ogDescription,
    ogImage,
    ogImageUrl,
    ogTitle,
  } = blogPost || {}

  // The Adjust tracking pixel (a 1px by 1px gif) hits an adjust server every time it's loaded. This is logged as an 'impression'
  // We only want this image to load once per page
  // This state conditionally renders the pixel
  const [firstLoad, setFirstLoad] = useState(true)

  const shareURL = `https://monzo.com${withPrefix(slug)}`
  const adjustedOGImageURL = idx(ogImage, _ => _.file.url)
    ? 'https:' + (idx(ogImage, _ => _.file.url) || '')
    : ogImageUrl

  const firstAuthor =
    authors != null ? generateAuthorProps(authors[0]) : undefined
  const firstCategoryName = idx(category, _ => _[0].categoryName) || ''
  const categoryLink = `/${slugifyString(firstCategoryName) || ''}`

  // This is the only template that fires google analytics. Other templates are covered from the gatsby-browser.js file
  // This template fires it's own google analytics as we need to furnish the page view with extra parameters that aren't available in the gatsby-browser.js file
  useEffect(() => {
    setDimensionsFirePageview(
      {
        dimension4: 'post',
        dimension5: firstCategoryName,
        dimension6: title,
        dimension7: firstAuthor,
      },
      pathname,
    )
    // The local gatsby-plugin-discourse creates a __discourseEmbed__ function that we can call
    // It will then inject the discourse comments into the #discourse-comments container on the page
    window &&
      window.__discourseEmbed__ &&
      window.__discourseEmbed__(discussionId)

    // watch out! https://github.com/facebook/create-react-app/issues/6880#issuecomment-485912528
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const content = blogContent && blogContent.content
  useEffect(() => {
    // Call the twitter widget loader when the component mounts
    window &&
      window.twttr &&
      window.twttr.widgets &&
      typeof window.twttr.widgets.load === 'function' &&
      window.twttr.widgets.load()
  }, [content])

  useEffect(() => {
    // When the component mounts for the first time...
    setFirstLoad(false)
  }, [])

  if (!blogContent || !(title && startDate && category && blogContent.content))
    return null

  const adjustTrackingProps = { monzo_referrer: webSlug || '' }
  const isUSPage = slugifyString(firstCategoryName) === 'usa'

  return (
    <Global
      pageMetadata={
        <SEOHead
          imageUrl={adjustedOGImageURL}
          description={
            (ogDescription && ogDescription.text) ||
            (description && description.description)
          }
          pageTitle={title}
          ogTitle={ogTitle}
          pageUrl={shareURL}
          locale={isUSPage ? 'en-US' : 'en-GB'}
        />
      }
      isUSPage={isUSPage}
    >
      <BlogHeader
        category={{ name: firstCategoryName, link: categoryLink }}
        publishedDate={startDate}
      >
        {title}
      </BlogHeader>
      <div>
        <article id="article" className={styles.blogContent}>
          {richTextToReact({ slug: webSlug || '' }, blogContent.content || '')}
          <SocialWidget shareURL={shareURL} />
        </article>
        <aside className={styles.authorAside}>
          {authors &&
            authors.map(author =>
              author != null ? (
                <div>
                  <Author
                    key={author.name}
                    author={generateAuthorProps(author)}
                  />
                </div>
              ) : null,
            )}
        </aside>
        {discussionId && (
          <div id="discourse-comments" className={styles.comments} />
        )}
      </div>
      {firstLoad && <AdjustImpressionPixel queryParams={adjustTrackingProps} />}
    </Global>
  )
}

export default BlogPost

/***************************
 *          QUERY
 ***************************/

export const query = graphql`
  query singleBlogPost($slug: String!) {
    contentfulWebBlogPost(webSlug: { eq: $slug }) {
      id
      webSlug
      title: blogTitle
      description {
        description
      }
      slug: webSlug
      startDate
      isFeaturedPost
      authors {
        id
        name
        jobTitle {
          text: jobTitle
        }
        profilePicture {
          file {
            url
          }
        }
      }
      blogContent {
        content: json
      }
      ogTitle
      ogDescription {
        text: ogDescription
      }
      ogImage {
        file {
          url
        }
      }
      ogImageUrl
      category {
        categoryName
        description {
          text: description
        }
        image {
          file {
            url
          }
        }
      }
      discussionId
    }
  }
`
